import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const EditContentTrack = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData, isSuperAdmin } = useAuth();
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('content'); // 'content', 'settings', 'preview'
  const [contentPieces, setContentPieces] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [unarchiveModalOpen, setUnarchiveModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);

  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
        if (!trackDoc.exists()) {
          navigate('/content-tracks');
          return;
        }

        const trackData = trackDoc.data();
        // Ensure visibility object exists with defaults
        const trackWithDefaults = {
          ...trackData,
          visibility: {
            isPublic: false,
            orgOnly: true,
            ...trackData.visibility
          }
        };
        setTrack(trackWithDefaults);

        // Fetch resources from subcollection (this is where content pieces are actually stored)
        try {
          const resourcesQuery = query(
            collection(db, 'contentTracks', trackId, 'resources'),
            orderBy('displayOrder', 'asc')
          );
          
          const resourcesSnapshot = await getDocs(resourcesQuery);
          const resourcesList = resourcesSnapshot.docs.map((doc, index) => ({
            id: doc.id,
            ...doc.data(),
            order: doc.data().displayOrder || index + 1,
            displayOrder: doc.data().displayOrder || index + 1
          }));

          setContentPieces(resourcesList);
        } catch (error) {
          // If displayOrder doesn't exist, fetch without ordering and add it
          console.log('No displayOrder field, fetching all resources and adding order...');
          const resourcesSnapshot = await getDocs(collection(db, 'contentTracks', trackId, 'resources'));
          const resourcesList = resourcesSnapshot.docs.map((doc, index) => ({
            id: doc.id,
            ...doc.data(),
            order: index + 1,
            displayOrder: index + 1
          }));

          // Update all resources with displayOrder
          const updatePromises = resourcesList.map((resource, index) =>
            updateDoc(doc(db, 'contentTracks', trackId, 'resources', resource.id), {
              displayOrder: index + 1
            })
          );
          await Promise.all(updatePromises);

          setContentPieces(resourcesList);
        }
      } catch (error) {
        console.error('Error fetching track:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrack();
  }, [trackId, navigate]);

  const getResourceEditPath = (resourceId) =>
    track?.trackType === 'children'
      ? `/content-tracks/${trackId}/resources/${resourceId}/children/edit`
      : `/content-tracks/${trackId}/resources/${resourceId}/edit`;

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(contentPieces);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index + 1,
      displayOrder: index + 1
    }));

    setContentPieces(updatedItems);

    // Update displayOrder in Firestore resources subcollection
    try {
      const batch = updatedItems.map((item, index) => 
        updateDoc(doc(db, 'contentTracks', trackId, 'resources', item.id), {
          displayOrder: index + 1
        })
      );
      await Promise.all(batch);
    } catch (error) {
      console.error('Error updating content order:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Create an update object with only the fields we want to update
      const updateData = {
        name: track?.name || '',
        description: track?.description || '',
        visibility: {
          isPublic: track?.visibility?.isPublic || false,
          orgOnly: track?.visibility?.orgOnly !== false // Default to true if not set
        },
        // Note: contentPieces array is maintained for backward compatibility
        // but actual content is stored in resources subcollection
        contentPieces: contentPieces.map((piece, index) => ({
          pieceId: piece.id,
          order: index + 1,
          isRequired: piece.isRequired || false
        })),
        lastModified: new Date(),
        lastModifiedBy: {
          userId: currentUser?.uid || '',
          name: userData?.name || ''
        }
      };

      // Log the data being saved for debugging
      console.log('Saving track data:', updateData);

      await updateDoc(doc(db, 'contentTracks', trackId), updateData);
      navigate('/content-tracks');
    } catch (error) {
      console.error('Error saving track:', error);
      // Optionally add user feedback here
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        status: 'published',
        publishedAt: new Date()
      });
      navigate('/content-tracks');
    } catch (error) {
      console.error('Error publishing track:', error);
    }
  };

  const handleApprove = async () => {
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        approvalStatus: 'approved',
        status: 'published',
        approvedBy: currentUser.uid,
        approvedAt: new Date()
      });
      setTrack(prev => ({ ...prev, approvalStatus: 'approved', status: 'published' }));
    } catch (error) {
      console.error('Error approving track:', error);
      alert('Failed to approve track');
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        approvalStatus: 'rejected',
        rejectedBy: currentUser.uid,
        rejectedAt: new Date(),
        rejectionReason: reason || ''
      });
      setTrack(prev => ({ ...prev, approvalStatus: 'rejected', rejectionReason: reason || '' }));
    } catch (error) {
      console.error('Error rejecting track:', error);
      alert('Failed to reject track');
    }
  };

  const handleArchiveTrack = async () => {
    if (!currentUser || !trackId) return;
    setArchiving(true);
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        status: 'archived',
        archivedAt: new Date(),
        archivedBy: currentUser.uid
      });
      setArchiveModalOpen(false);
      navigate('/content-tracks');
    } catch (error) {
      console.error('Error archiving track:', error);
      alert('Failed to archive track. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveTrack = async () => {
    if (!currentUser || !trackId) return;
    setUnarchiving(true);
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        status: 'draft',
        archivedAt: null,
        archivedBy: null
      });
      setUnarchiveModalOpen(false);
      setTrack(prev => ({
        ...prev,
        status: 'draft',
        archivedAt: null,
        archivedBy: null
      }));
    } catch (error) {
      console.error('Error unarchiving track:', error);
      alert('Failed to unarchive track. Please try again.');
    } finally {
      setUnarchiving(false);
    }
  };

  const renderPrimaryActions = () => (
    <>
      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving}
        className="px-4 py-2 bg-[#0076BE] text-white rounded-md hover:bg-[#0065a3] disabled:opacity-50 flex items-center gap-2"
      >
        {isSaving ? 'Saving...' : 'Save Changes'}
      </button>
      {track?.status === 'draft' && (
        <button
          type="button"
          onClick={handlePublish}
          className="px-4 py-2 bg-[#5ABA47] text-white rounded-md hover:bg-[#4a9939] flex items-center gap-2"
        >
          Publish Track
        </button>
      )}
      {isSuperAdmin() && track?.status !== 'archived' && (
        <button
          type="button"
          onClick={() => setArchiveModalOpen(true)}
          className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
        >
          Archive Track
        </button>
      )}
      {isSuperAdmin() && track?.status === 'archived' && (
        <button
          type="button"
          onClick={() => setUnarchiveModalOpen(true)}
          className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700"
        >
          Unarchive Track
        </button>
      )}
    </>
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            {/* Loading skeleton */}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Unified wayfinding: list ↔ overview ↔ edit */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              type="button"
              onClick={() => navigate('/content-tracks')}
              className="text-gray-700 hover:text-gray-900 font-medium inline-flex items-center gap-2 text-sm"
            >
              <span aria-hidden="true">←</span>
              Back to all Content Tracks
            </button>
            <span className="hidden sm:inline text-gray-300 select-none" aria-hidden="true">
              |
            </span>
            <button
              type="button"
              onClick={() => navigate(`/content-tracks/${trackId}`)}
              className="text-sm text-[#0076BE] hover:text-[#005c94] font-medium hover:underline"
            >
              View track overview
            </button>
          </div>
          <p className="text-sm text-gray-600 sm:text-right max-w-xl">
            <span className="font-semibold text-gray-800">Edit track</span>
            {' — '}
            Add resources, bulk upload, and set order. The overview page is for previewing the track for a disciple.
          </p>
        </div>

        {/* Approval Status Banner */}
        {track?.approvalStatus === 'pending' && (
          <div className="mb-6 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending Approval
                </span>
                <p className="text-sm text-yellow-800">
                  This track is awaiting super admin approval before it can be published.
                </p>
              </div>
              {isSuperAdmin() && (
                <div className="flex gap-2 ml-4 shrink-0">
                  <button
                    onClick={handleApprove}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    Approve &amp; Publish
                  </button>
                  <button
                    onClick={handleReject}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {track?.approvalStatus === 'rejected' && (
          <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Rejected
              </span>
              <p className="text-sm text-red-800">
                This track was rejected.
                {track.rejectionReason && <span className="font-medium"> Reason: {track.rejectionReason}</span>}
              </p>
            </div>
            {isSuperAdmin() && (
              <div className="mt-3">
                <button
                  onClick={handleApprove}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Approve &amp; Publish
                </button>
              </div>
            )}
          </div>
        )}
        {track?.approvalStatus === 'approved' && track?.status !== 'published' && track?.status !== 'active' && (
          <div className="mb-6 rounded-lg border border-green-300 bg-green-50 p-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Approved
              </span>
              <p className="text-sm text-green-800">
                This track has been approved but is not yet published.
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1 mr-4">
              <input
                type="text"
                value={track?.name || ''}
                onChange={(e) => setTrack(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
                className="text-3xl font-bold text-gray-900 w-full px-2 py-1 border-b border-transparent hover:border-gray-300 focus:border-[#0076BE] focus:outline-none"
                placeholder="Track Name"
              />
              <textarea
                value={track?.description || ''}
                onChange={(e) => setTrack(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
                className="text-gray-500 mt-2 w-full px-2 py-1 border-b border-transparent hover:border-gray-300 focus:border-[#0076BE] focus:outline-none resize-none"
                placeholder="Track Description"
                rows={2}
              />
            </div>
            <div className="flex flex-wrap gap-3 justify-end">
              {renderPrimaryActions()}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8" aria-label="Track editor sections">
            {[
              { id: 'content', label: 'Content' },
              { id: 'settings', label: 'Settings' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div>
            {/* Add Content Actions */}
            <div className="bg-white rounded-lg shadow p-4 mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Track Content</h3>
                <p className="text-sm text-gray-500">
                  Add resources to this track, then drag to reorder them below.
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                {track?.trackType === 'children' ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/content-tracks/${trackId}/resources/children/create`)}
                    className="px-4 py-2 text-white bg-[#0076BE] rounded hover:bg-[#005c94]"
                  >
                    Add Children&apos;s Resource
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate(`/content-tracks/${trackId}/resources/create`)}
                    className="px-4 py-2 text-white bg-[#5ABA47] rounded hover:bg-[#4a9939]"
                  >
                    Add Resource
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/content-tracks/${trackId}/resources/upload`)}
                  className="px-4 py-2 text-white bg-[#0076BE] rounded hover:bg-[#0065a3]"
                >
                  Bulk upload
                </button>
              </div>
            </div>

            {/* Track Content List */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Track Content Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Click a resource to open its editor. Drag the numbered handle on the left to reorder.
              </p>
              {contentPieces.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                  <p className="text-gray-500">No content added yet. Use &ldquo;Add Resource&rdquo; or &ldquo;Bulk upload&rdquo; above to add content to this track.</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="track-content">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-3"
                      >
                        {contentPieces.map((piece, index) => (
                          <Draggable
                            key={piece.id}
                            draggableId={piece.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-start justify-between gap-3 ${
                                  snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-500' : ''
                                }`}
                              >
                                <div className="flex items-start space-x-3 flex-1 min-w-0">
                                  <div
                                    {...provided.dragHandleProps}
                                    className="flex-shrink-0 mt-1 cursor-grab active:cursor-grabbing rounded-md p-0.5 hover:bg-gray-100"
                                    title="Drag to reorder"
                                  >
                                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                      <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => navigate(getResourceEditPath(piece.id))}
                                    className="flex-1 min-w-0 text-left rounded-md -m-1 p-1 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0076BE] focus-visible:ring-offset-2"
                                  >
                                    <h4 className="font-medium text-gray-900 mb-1">{piece.title}</h4>
                                    {piece.description && (
                                      <p className="text-sm text-gray-500 line-clamp-2">{piece.description}</p>
                                    )}
                                    <div className="flex items-center space-x-3 mt-2">
                                      <span className="text-xs text-gray-400">{piece.type}</span>
                                      {piece.difficulty && (
                                        <span className="text-xs text-gray-400">
                                          Difficulty: {piece.difficulty}/5
                                        </span>
                                      )}
                                    </div>
                                    <span className="mt-2 inline-block text-xs font-medium text-[#0076BE]">
                                      Edit resource →
                                    </span>
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await deleteDoc(doc(db, 'contentTracks', trackId, 'resources', piece.id));

                                      setContentPieces((prev) => prev.filter((p) => p.id !== piece.id));

                                      const remaining = contentPieces.filter((p) => p.id !== piece.id);
                                      const batch = remaining.map((item, idx) =>
                                        updateDoc(doc(db, 'contentTracks', trackId, 'resources', item.id), {
                                          displayOrder: idx + 1
                                        })
                                      );
                                      await Promise.all(batch);

                                      setContentPieces(
                                        remaining.map((item, idx) => ({
                                          ...item,
                                          order: idx + 1,
                                          displayOrder: idx + 1
                                        }))
                                      );
                                    } catch (error) {
                                      console.error('Error removing resource:', error);
                                      alert('Failed to remove content piece. Please try again.');
                                    }
                                  }}
                                  className="shrink-0 text-red-600 hover:text-red-800 text-sm font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-3xl">
            {/* Track Settings Form */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Track Name</label>
                    <input
                      type="text"
                      value={track.name}
                      onChange={(e) => setTrack(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={track.description}
                      onChange={(e) => setTrack(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Visibility Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Who can access this track?
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibility"
                          value="public"
                          checked={track?.visibility?.isPublic === true}
                          onChange={async (e) => {
                            const newVisibility = {
                              isPublic: true,
                              orgOnly: false
                            };
                            
                            // Update local state immediately
                            setTrack(prev => ({
                              ...prev,
                              visibility: newVisibility
                            }));
                            
                            // Auto-save visibility change
                            try {
                              await updateDoc(doc(db, 'contentTracks', trackId), {
                                visibility: newVisibility,
                                lastModified: new Date(),
                                lastModifiedBy: {
                                  userId: currentUser?.uid || '',
                                  name: userData?.name || ''
                                }
                              });
                              console.log('Visibility updated:', newVisibility);
                            } catch (error) {
                              console.error('Error updating visibility:', error);
                              alert('Failed to update visibility. Please try again.');
                              // Revert on error
                              setTrack(prev => ({
                                ...prev,
                                visibility: prev?.visibility || { isPublic: false, orgOnly: true }
                              }));
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-medium text-gray-700">Public</span>
                          <p className="text-xs text-gray-500">Available to all TOM users</p>
                        </div>
                      </label>
                      
                      {track?.organizationId && (
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="visibility"
                            value="orgOnly"
                            checked={track?.visibility?.isPublic === false && track?.visibility?.orgOnly === true}
                            onChange={async (e) => {
                              const newVisibility = {
                                isPublic: false,
                                orgOnly: true
                              };
                              
                              // Update local state immediately
                              setTrack(prev => ({
                                ...prev,
                                visibility: newVisibility
                              }));
                              
                              // Auto-save visibility change
                              try {
                                await updateDoc(doc(db, 'contentTracks', trackId), {
                                  visibility: newVisibility,
                                  lastModified: new Date(),
                                  lastModifiedBy: {
                                    userId: currentUser?.uid || '',
                                    name: userData?.name || ''
                                  }
                                });
                                console.log('Visibility updated:', newVisibility);
                              } catch (error) {
                                console.error('Error updating visibility:', error);
                                alert('Failed to update visibility. Please try again.');
                                // Revert on error
                                setTrack(prev => ({
                                  ...prev,
                                  visibility: prev?.visibility || { isPublic: true, orgOnly: false }
                                }));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-700">Organization Only</span>
                            <p className="text-xs text-gray-500">Available only to your organization members</p>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Target Audience */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Target Audience</h3>
                {/* ... similar to CreateContentTrack form ... */}
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-gray-500">
            Save returns you to the Content Tracks list. Use overview to preview without leaving the browser tab.
          </p>
          <div className="flex flex-wrap gap-3 justify-end shrink-0">
            {renderPrimaryActions()}
          </div>
        </div>

        {archiveModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Archive Track
              </h3>
              <p className="text-gray-600 mb-6">
                This will archive &quot;<strong>{track?.name}</strong>&quot; and hide it from the active list. You can view it under the Archived tab.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setArchiveModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                  disabled={archiving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleArchiveTrack}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
                  disabled={archiving}
                >
                  {archiving ? 'Archiving...' : 'Archive'}
                </button>
              </div>
            </div>
          </div>
        )}

        {unarchiveModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Unarchive Track
              </h3>
              <p className="text-gray-600 mb-6">
                This will move &quot;<strong>{track?.name}</strong>&quot; back to the active list.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setUnarchiveModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
                  disabled={unarchiving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUnarchiveTrack}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                  disabled={unarchiving}
                >
                  {unarchiving ? 'Unarchiving...' : 'Unarchive'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EditContentTrack; 